import React, { forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import TextAreaAutosize from 'react-autosize-textarea';

import InfoMessage from '../../../components/infoMessage';
import { TextAreaProps } from '../Inputs.types';

import './Textarea.scss';

const TextArea = forwardRef((props: TextAreaProps, forwardedRef) => {
    const { className, error, errorMessage, id, label, limit, placeholder, rows = 3, value, ...rest } = props;

    const inputRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => inputRef.current);
    const focusInput = () => {
        inputRef.current?.focus();
    };

    const inputId = useMemo(() => id || uniqueId('textarea-input-'), [id]);
    const detailsId = useMemo(() => uniqueId('textarea-input-details-'), []);

    const fieldClassName = classNames(className, 'ds-textarea-field', { error: error });
    const FieldWrapperClassName = classNames('ds-textarea-field__wrapper', {
        'ds-textarea-field__wrapper--error': error || errorMessage,
    });
    const InputClassName = classNames('ds-textarea-field__wrapper__input', {
        error: error,
    });

    return (
        <div className={fieldClassName} onClick={focusInput}>
            <div className={FieldWrapperClassName}>
                <TextAreaAutosize
                    {...rest}
                    aria-describedby={`${detailsId} ${inputId}`}
                    className={InputClassName}
                    form={undefined}
                    id={inputId}
                    onResize={() => undefined}
                    placeholder=" "
                    ref={inputRef}
                    rows={rows}
                    value={value}
                />
                <label htmlFor={inputId} className="ds-textarea-field__wrapper__label">
                    {label}
                </label>
                {!!limit && (
                    <div
                        className={classNames(
                            'ds-text-field__wrapper__limit',
                            (value?.toString().length ?? 0) > limit && 'max'
                        )}
                    >
                        {value?.toString().length.toLocaleString() ?? 0} / {limit.toLocaleString()}
                    </div>
                )}
            </div>
            {props['aria-describedby'] && (
                <p className="ds-textarea-field__message ds-textarea-field__message--helpText" id={detailsId}>
                    {props['aria-describedby']}
                </p>
            )}
            {errorMessage && (
                <InfoMessage
                    className="ds-textarea-field__message ds-textarea-field__message--error"
                    type="error"
                    message={errorMessage}
                />
            )}
        </div>
    );
});

export default TextArea;
